/**
 * Word-level diff for Blayzrr's Profile Rewrite "Before / After" tiles.
 * Powered by the standard Longest-Common-Subsequence algorithm on
 * whitespace-tokenised words. Punctuation stays attached to the
 * preceding token so highlighted spans read like real prose ("word."
 * rather than "word" | "."). Case-insensitive comparison so simple
 * capitalisation changes don't flood the highlight.
 *
 * Returns two arrays of tokens with kinds:
 *   before[i] = { text, kind: 'equal' | 'removed' }
 *   after[i]  = { text, kind: 'equal' | 'added' }
 * Consumers wrap `removed` in a strike-through red span and `added`
 * in a green highlight.
 */

function tokenise(str) {
  if (!str) return [];
  // Keep leading whitespace on each token so we can render tokens
  // sequentially without collapsing runs of spaces or newlines.
  const out = [];
  const re = /(\s*)(\S+)/g;
  let m;
  while ((m = re.exec(str)) !== null) {
    out.push({ ws: m[1], word: m[2] });
  }
  // Preserve trailing whitespace if the string ends with one.
  const trailing = str.match(/\s+$/);
  if (trailing) out.push({ ws: trailing[0], word: '' });
  return out;
}

function norm(tok) {
  // Strip attached punctuation for comparison so "sales." matches "sales".
  return (tok.word || '').toLowerCase().replace(/[.,;:!?"'()[\]—–-]+$/g, '').replace(/^[.,;:!?"'()[\]—–-]+/g, '');
}

function lcs(a, b) {
  const n = a.length, m = b.length;
  if (!n || !m) return [];
  // Dynamic-programming table of LCS lengths.
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (norm(a[i - 1]) === norm(b[j - 1])) dp[i][j] = dp[i - 1][j - 1] + 1;
      else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  // Backtrack.
  const pairs = [];
  let i = n, j = m;
  while (i > 0 && j > 0) {
    if (norm(a[i - 1]) === norm(b[j - 1])) {
      pairs.unshift([i - 1, j - 1]);
      i--; j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) i--;
    else j--;
  }
  return pairs;
}

export function wordDiff(before, after) {
  const a = tokenise(before || '');
  const b = tokenise(after || '');
  if (!a.length && !b.length) return { before: [], after: [] };
  if (!a.length) {
    return { before: [], after: b.map((t) => ({ ws: t.ws, text: t.word, kind: 'added' })) };
  }
  if (!b.length) {
    return { before: a.map((t) => ({ ws: t.ws, text: t.word, kind: 'removed' })), after: [] };
  }
  const pairs = lcs(a, b);
  const matchedA = new Set(pairs.map((p) => p[0]));
  const matchedB = new Set(pairs.map((p) => p[1]));
  const beforeOut = a.map((t, idx) => ({
    ws: t.ws,
    text: t.word,
    kind: matchedA.has(idx) ? 'equal' : 'removed',
  }));
  const afterOut = b.map((t, idx) => ({
    ws: t.ws,
    text: t.word,
    kind: matchedB.has(idx) ? 'equal' : 'added',
  }));
  return { before: beforeOut, after: afterOut };
}
